import {
  Component,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BookingService } from '@app/booking/booking.service';
import { Booking, BookingTypes } from '@app/booking/booking.types';
import { BookingsListResponse } from '@app/booking/booking-list-container/booking-list-container.component';
import { I18nService } from '@app/i18n';
import { debounceTime, finalize } from 'rxjs/operators';
import { DashboardService, LuggageStage } from './dashboard.service';
import {
  ChartStageData,
  ComparativeChartItem,
  DashboardServiceFilterOption,
  LandingData,
} from './dashboard.types';
import { EBookingSearchStatus } from '@app/booking/statuses';
import {
  Chart,
  ChartConfiguration,
  ChartData,
  ChartType,
  Plugin,
} from 'chart.js';

import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [
    {
      provide: LOCALE_ID,
      deps: [I18nService],
      useFactory: (i18nService: I18nService) => {
        return i18nService.language;
      },
    },
  ],
  standalone: false,
})
export class DashboardComponent implements OnInit {
  landingData: LandingData | undefined;
  bookings: Booking[] = [];
  dir: string;
  bookingsLoader: boolean = false;
  showAnalytics = environment.enableCustomerAnalytics;
  has5bookings = false;
  comparativeData: ComparativeChartItem[] = [];
  services: DashboardServiceFilterOption[] = [
    {
      id: '',
      title: this.translateService.instant('All Bookings'),
    },
    {
      id: 3,
      title: this.translateService.instant('Airport-To-Door - Arrival Service'),
    },
    {
      id: 2,
      title: this.translateService.instant(
        'Door-to-Airport - Departure Service',
      ),
    },
    {
      id: 1,
      title: this.translateService.instant('Door-To-Door'),
    },
  ];
  isComparativeChartLoading = true;

  comparativeTypeFiltersForm: FormGroup = new FormGroup({
    bookingTypeId: new FormControl(null, { validators: [] }),
  });

  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 5,
        },
        grid: {
          display: false,
        },
      },
      y: {
        stacked: false,
        afterDataLimits: (scale) => {
          scale.max *= 1.1;
        },
        ticks: {
          callback: (value) => {
            return this.dashboardService.formatTime(Number(value));
          },
          maxTicksLimit: 6,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 10,
          font: {
            size: window.innerWidth < 768 ? 10 : 12,
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: true,
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label || '';
            const value = Number(context.parsed.y);
            return `${datasetLabel}: ${this.dashboardService.formatTime(
              isNaN(value) ? 0 : value,
            )}`;
          },
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        right: 10,
        left: 15,
        bottom: 10,
      },
    },
  };
  public barChartPlugins: Plugin<'bar'>[] = [
    {
      id: 'dashboardBarChartLabels',
      beforeDraw: (chart: Chart<'bar'>) => {
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;

        if (!chartArea) {
          return;
        }

        ctx.save();

        // Check if we're on mobile
        const isMobile = window.innerWidth < 768;

        // Customize text properties
        ctx.font = isMobile ? 'bold 10px Arial' : 'bold 14px Arial';
        ctx.fillStyle = '#000';
        ctx.textAlign = 'center';

        if (!isMobile) {
          // Draw "Stages" centered horizontally below the chart
          const stagesLabel = this.translateService.instant('Stages');
          const centerX = (chartArea.left + chartArea.right) / 2;
          const centerY = chartArea.bottom + 25; // adjust offset as needed

          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillText(stagesLabel, centerX, centerY);

          ctx.save();

          const timeLabel = this.translateService.instant('Time Spent');
          const x = chartArea.left - 80;
          const y = (chartArea.top + chartArea.bottom) / 2;

          // Move origin to left-center, rotate 90deg counter-clockwise and draw
          ctx.translate(x, y);
          ctx.rotate(-Math.PI / 2);
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(timeLabel, 0, 0);
          ctx.restore();
        }

        ctx.restore();
      },
      afterDatasetsDraw: (chart: Chart<'bar'>) => {
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const datasets = chart.data.datasets;
        const isMobile = window.innerWidth < 768;

        if (
          (chart.options?.scales?.['x'] as { stacked?: boolean | 'single' })
            ?.stacked
        )
          return;

        if (isMobile) return;

        datasets.forEach((dataset, datasetIndex: number) => {
          const meta = chart.getDatasetMeta(datasetIndex);

          meta.data.forEach((bar, index: number) => {
            if (!bar) return;

            const value = dataset.data[index];
            if (!value) return;

            const x = bar.x;

            // Don't draw if legend is hidden
            if (chart.legend?.legendItems?.[datasetIndex]?.hidden) return;

            ctx.fillStyle = dataset.backgroundColor as
              string | CanvasGradient | CanvasPattern;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(dataset.label, x, chartArea.bottom + 12);
          });
        });
      },
    },
  ];
  public barChartType: ChartType = 'bar';

  public barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        label: this.translateService.instant('Luggage Pickup'),
        backgroundColor: '#FB773C',
        hoverBackgroundColor: '#FB773C',
        data: [],
      },
      {
        label: this.translateService.instant('Luggage Checkin'),
        backgroundColor: '#EB3678',
        hoverBackgroundColor: '#EB3678',
        data: [],
      },
      {
        label: this.translateService.instant('Luggage Transit'),
        backgroundColor: '#4F1787',
        hoverBackgroundColor: '#4F1787',
        data: [],
      },
      {
        label: this.translateService.instant('Luggage Checkout'),
        backgroundColor: '#180161',
        hoverBackgroundColor: '#180161',
        data: [],
      },
      {
        label: this.translateService.instant('Luggage Delivery'),
        backgroundColor: '#59D5E0',
        hoverBackgroundColor: '#59D5E0',
        data: [],
      },
    ],
  };

  constructor(
    private dashboardService: DashboardService,
    private bookingService: BookingService,
    private translateService: TranslateService,
    private router: Router,
  ) {}

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.dir = localStorage.getItem('html-dir');
    this.dashboardService
      .getLanding(0, 0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: LandingData) => {
        this.landingData = response;
      });
    this.setupComparativeTypeFiltersForm();
    this.getBookings();
    this.getComparativeChartDetails();
  }

  setupComparativeTypeFiltersForm() {
    this.comparativeTypeFiltersForm.valueChanges
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.comparativeTypeFiltersForm.invalid) return;
        this.updateChartData(value?.bookingTypeId);
      });
  }

  // Dashboard only shows the top 5 active bookings (no pagination).
  private getBookings() {
    this.bookingsLoader = true;
    this.bookingService
      .getBookings({
        statusGroupId: EBookingSearchStatus.Active,
        pageNumber: 1,
        pageSize: 5,
      })
      .pipe(
        finalize(() => {
          this.bookingsLoader = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: BookingsListResponse) => {
        this.bookings = res?.data ?? [];
        // show analytics when there are at least 5 bookings
        this.has5bookings = this.bookings.length >= 5;
      });
  }

  getBookingTypeLabel(bookingTypeId: number) {
    if (
      bookingTypeId &&
      BookingTypes[bookingTypeId as keyof typeof BookingTypes]
    ) {
      return BookingTypes[bookingTypeId as keyof typeof BookingTypes];
    }
    return '';
  }

  getBookingSymbol(bookingTypeId: number) {
    switch (bookingTypeId) {
      case 1:
        return '/assets/services/d2d-symbol.svg';
      case 2:
        return '/assets/services/d2a-symbol.svg';
      case 3:
        return '/assets/services/a2d-symbol.svg';
    }
  }

  createBooking() {
    this.router.navigateByUrl('/bookings/create');
  }

  goToBookings() {
    this.router.navigateByUrl('/bookings');
  }

  goToBooking(bookingId: number) {
    this.router.navigateByUrl(`/bookings/${bookingId}`);
  }

  getComparativeChartDetails() {
    if (!this.showAnalytics) return;
    this.isComparativeChartLoading = true;
    this.dashboardService
      .getComparativeChart()
      .pipe(
        finalize(() => {
          this.isComparativeChartLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.comparativeData = res;
        this.updateChartData();
      });
  }

  updateChartData(bookingTypeId = 0) {
    const data = bookingTypeId
      ? this.comparativeData.filter(
          (cData) => cData.bookingTypeId == bookingTypeId,
        )
      : this.comparativeData;
    const convertedData = this.convertDataToChartFormat(data);
    this.fillChartData(convertedData);
  }

  fillChartData(data: ChartStageData[]) {
    const labels: string[] = [];
    const pickup: number[] = [];
    const checkin: number[] = [];
    const transit: number[] = [];
    const checkout: number[] = [];
    const delivery: number[] = [];

    data.forEach((item) => {
      labels.push(item.title);
      pickup.push(item[LuggageStage.Pickup]);
      checkin.push(item[LuggageStage.Checkin]);
      transit.push(item[LuggageStage.Transit]);
      checkout.push(item[LuggageStage.Checkout]);
      delivery.push(item[LuggageStage.Delivery]);
    });

    this.barChartData = {
      labels,
      datasets: [
        { ...this.barChartData.datasets[0], data: pickup },
        { ...this.barChartData.datasets[1], data: checkin },
        { ...this.barChartData.datasets[2], data: transit },
        { ...this.barChartData.datasets[3], data: checkout },
        { ...this.barChartData.datasets[4], data: delivery },
      ],
    };
  }

  convertDataToChartFormat(data: ComparativeChartItem[]): ChartStageData[] {
    const stages = {
      [LuggageStage.Pickup]: 0,
      [LuggageStage.Checkin]: 0,
      [LuggageStage.Transit]: 0,
      [LuggageStage.Checkout]: 0,
      [LuggageStage.Delivery]: 0,
    };

    data.forEach((item) => {
      stages[item.stage as LuggageStage] += item.timeSpent;
    });

    return [{ title: '', ...stages }];
  }
}
